import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterListIcon from "@mui/icons-material/FilterList";
import { IChangeHistoryFilters } from "./ChangeHistoryTimeline.types";
import { DEFAULT_FILTERS } from "./constants";

interface IFilterPanelProps {
  onApplyFilters: (filters: IChangeHistoryFilters) => void;
  onClearFilters: () => void;
  /** When true, hides the Entry ID filter (entry_id is already provided via props) */
  hideEntryIdFilter?: boolean;
}

export const FilterPanel: React.FC<IFilterPanelProps> = ({
  onApplyFilters,
  onClearFilters,
  hideEntryIdFilter,
}) => {
  const [filters, setFilters] = useState<IChangeHistoryFilters>({
    ...DEFAULT_FILTERS,
  });

  const handleChange = (field: keyof IChangeHistoryFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    onApplyFilters(filters);
  };

  const handleClear = () => {
    setFilters({ ...DEFAULT_FILTERS });
    onClearFilters();
  };

  return (
    <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="filter-panel-content"
        id="filter-panel-header"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon fontSize="small" />
          <Typography variant="subtitle2">Filters</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="From Date"
              type="date"
              size="small"
              fullWidth
              value={filters.from_date}
              onChange={(e) => handleChange("from_date", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "From Date" }}
            />
            <TextField
              label="To Date"
              type="date"
              size="small"
              fullWidth
              value={filters.to_date}
              onChange={(e) => handleChange("to_date", e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "To Date" }}
            />
          </Box>

          <FormControl size="small" fullWidth>
            <InputLabel id="action-type-label">Action Type</InputLabel>
            <Select
              labelId="action-type-label"
              label="Action Type"
              value={filters.action}
              onChange={(e) => handleChange("action", e.target.value)}
              inputProps={{ "aria-label": "Action Type" }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="UPDATED">UPDATED</MenuItem>
              <MenuItem value="CREATED">CREATED</MenuItem>
              <MenuItem value="DELETED">DELETED</MenuItem>
            </Select>
          </FormControl>

          {!hideEntryIdFilter && (
            <TextField
              label="Entry ID"
              size="small"
              fullWidth
              value={filters.entry_id}
              onChange={(e) => handleChange("entry_id", e.target.value)}
              inputProps={{ "aria-label": "Entry ID" }}
            />
          )}

          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClear}
              aria-label="Clear Filters"
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              aria-label="Apply Filters"
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
